/**
 * Pure helpers for feature requests: status and type tones, the "Which part
 * of the app?" options, and the page context sent with a request. No PHI:
 * the route is the manifest pattern (`/patients/$patientId`), or the
 * pathname with every id-like segment replaced.
 */
import {
  sanitizeRoutePath,
  type FeedbackClientContext,
  type FeedbackStatus,
  type FeedbackType,
} from '@velora/contracts/feedback';
import type { Tone } from '@velora/ui';

/** Status colours: no blue; yellow only as the "in progress" status. */
export const STATUS_TONE: Record<FeedbackStatus, Tone> = {
  new: 'brand',
  reviewing: 'neutral',
  planned: 'neutral',
  in_progress: 'warning',
  resolved: 'success',
  declined: 'neutral',
};

export const TYPE_TONE: Record<FeedbackType, Tone> = {
  bug: 'danger',
  improvement: 'neutral',
  feature: 'brand',
  other: 'neutral',
};

export interface AreaModule {
  id: string;
  title: string;
  hasNav: boolean;
}

export interface AreaOption {
  value: string;
  label: string;
}

/** Modules with a nav entry, by title, then "Other". */
export function areaOptions(modules: readonly AreaModule[]): AreaOption[] {
  const seen = new Set<string>();
  const out: AreaOption[] = [];
  for (const m of [...modules].sort((a, b) => a.title.localeCompare(b.title))) {
    if (!m.hasNav || seen.has(m.id) || m.id === 'feedback') continue;
    seen.add(m.id);
    out.push({ value: m.id, label: m.title });
  }
  out.push({ value: 'other', label: 'Other' });
  return out;
}

export interface ActiveRouteInfo {
  moduleId: string;
  route: { path: string; title: string };
}

/** Context sent with a new request: route pattern, title, module, viewport. */
export function buildClientContext(
  active: ActiveRouteInfo | undefined,
  pathname: string,
  viewport: { width: number; height: number } | null,
): FeedbackClientContext {
  const routePath = active ? sanitizeRoutePath(active.route.path) : sanitizeRoutePath(pathname);
  const moduleId =
    active && /^[a-z][a-z0-9-]{0,63}$/.test(active.moduleId) ? active.moduleId : null;
  return {
    routePath,
    routeTitle: active ? active.route.title.slice(0, 120) : null,
    moduleId,
    viewport: viewport ? `${Math.round(viewport.width)}x${Math.round(viewport.height)}` : null,
  };
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
